import PropTypes from "prop-types"
import { useState, useContext, useEffect, useCallback } from "react"
import { gql, useLazyQuery } from "@apollo/client"
import _ from "lodash"
import { Field, useFormikContext } from "formik"
import { MultiSelect } from "primereact/multiselect"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"
import { LoaderSmall } from "../../Loaders"
import { LogError } from "../../../helpers/logger"

import styles from "../../../styles/Form.module.css"
import { DateTime } from "luxon"
import ManageHistoryOverlap from "./shared/ManageHistoryOverlap"

function ManageProgramIdentityIds({ field, block, index }) {
  const formik = useFormikContext()
  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)
  const [activePrograms, setActivePrograms] = useState([])
  const [inactivePrograms, setInactivePrograms] = useState([])
  const [programHistory, setProgramHistory] = useState([])
  const [visible, setVisible] = useState("none")

  // Ensures array
  const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : [])

  // Cleans null / undefined / objects without key/label
  const cleanArray = (arr) => toArray(arr).filter((it) => it && it.key && it.label)

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }
  const isRequired = checkIfRequired()

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  /* INIT PROGRAMS */
  const GET_PROGRAMS = gql`
    query getPrograms(
      $staff_id: ID!
      $organization_id: ID!
      $participant_membership_id: ID!
    ) {
      programsByOrganization(
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        key: program_identity_id
        label: program_id_name
        status: program_id_status
      }
      programsByMembershipId(
        staff_id: $staff_id
        organization_id: $organization_id
        participant_membership_id: $participant_membership_id
      ) {
        key: program_identity_id
        label: program_id_name
      }
    }
  `

  const [getPrograms, { loading }] = useLazyQuery(GET_PROGRAMS, {
    fetchPolicy: "network-only",
  })

  async function initPrograms() {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    try {
      const { data } = await getPrograms({
        variables: {
          staff_id: userCtx?.session?.user?.staff_id,
          organization_id: userCtx?.session?.user?.organization_id,
          participant_membership_id: userCtx?.participant_membership_id,
        },
      })

      const { programsByOrganization, programsByMembershipId } = data || {}

      if (!_.isEmpty(programsByOrganization)) {
        const activeProgramsList =
          programsByOrganization
            .filter((program) => program.status === "ACTIVE")
            .map((program) => {
              // remove status, keep key/label
              const { status, ...rest } = program
              return rest
            }) || []

        const inactiveProgramsList =
          programsByOrganization
            .filter((program) => program.status === "INACTIVE")
            .map((program) => {
              const { status, ...rest } = program
              return rest
            }) || []

        setActivePrograms(activeProgramsList)
        setInactivePrograms(inactiveProgramsList)

        // Only set initial value if current value is empty
        const currentValue = formik.values[field.field_name]
        if (!currentValue || currentValue.length === 0) {
          // Normalize membership values so they match options by key
          const original = cleanArray(programsByMembershipId)
          const allPrograms = [...activeProgramsList, ...inactiveProgramsList]

          const normalized = original
            .map((item) => {
              const match = allPrograms.find((p) => p.key === item.key)
              return match ? match : item
            })
            .filter((v) => v && v.key && v.label)

          // Set normalized value
          formik.setFieldValue(field.field_name, normalized)
        }
      } else {
        // save empty array if nothing, avoids null
        formik.setFieldValue(field.field_name, [])
      }
    } catch (error) {
      LogError("Error fetching programs:", error)
      formik.setFieldValue(field.field_name, [])
    } finally {
      // Ensure loading state is cleared even if there's an error
      form && form.completeLoadingField(field)
    }
  }

  useEffect(() => {
    initPrograms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (block === "participant__manage_services") {
      const manageServicesType = formik?.getFieldProps("manage_services_type")
      setVisible("none")
      if (
        manageServicesType.value === "Program ID assignment" ||
        manageServicesType.value === "Program ID reassignment"
      ) {
        setVisible("block")
      }
    } else {
      setVisible("block")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik])

  const GET_PROGRAM_HISTORY = gql`
    query ProgramHistory(
      $participant_membership_id: ID!
      $effectiveDate: String!
    ) {
      getParticipantMembershipProgramHistoryAfterDate(
        participant_membership_id: $participant_membership_id
        effective_date: $effectiveDate
      ) {
        program_id_name
        program_identity_id
        start_date
        end_date
      }
    }
  `

  const [getProgramHistory] = useLazyQuery(GET_PROGRAM_HISTORY, {
    fetchPolicy: "cache-and-network",
    variables: {
      participant_membership_id: undefined,
      effectiveDate: undefined,
    },
  })

  const loadProgramHistory = useCallback(async () => {
    if (form.idTemplate === "manage_services") {
      const manageServicesDate = formik?.values?.manage_services_date

      if (manageServicesDate) {
        const result = await getProgramHistory({
          variables: {
            participant_membership_id: userCtx?.participant_membership_id,
            effectiveDate: manageServicesDate,
          },
        })

        if (result?.error) {
          LogError(`Error fetching program history: ${result.error.message}`)
        }

        if (result?.data?.getParticipantMembershipProgramHistoryAfterDate) {
          const history =
            result.data.getParticipantMembershipProgramHistoryAfterDate.map(
              (entry) => {
                const startDate = DateTime.fromMillis(Number(entry.start_date))
                const endDate = DateTime.fromMillis(Number(entry.end_date))

                return {
                  start_date: startDate.toJSDate(),
                  end_date: endDate.toJSDate(),
                  program_identity_id: entry.program_identity_id,
                  program_id_name: entry.program_id_name,
                }
              },
            )

          setProgramHistory(history)
        } else {
          setProgramHistory([])
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.idTemplate,
    formik?.values?.manage_services_date,
    userCtx?.participant_membership_id,
  ])

  useEffect(() => {
    loadProgramHistory()
  }, [loadProgramHistory])

  // Normalizes an array of items using current active/inactive lists
  const normalizeByKey = (items = []) => {
    const safe = cleanArray(items)
    return safe.map((item) => {
      const match =
        activePrograms.find((p) => p.key === item.key) ||
        inactivePrograms.find((p) => p.key === item.key)
      return match ? match : item
    })
  }

  const handleSelectProgramIds = (value) => {
    if (value === null) {
      formik.setFieldValue(field.field_name, "")
    } else {
      const fieldCurrentValue = formik.values[field.field_name] || []
      // keep previously selected inactive programs
      const currentInActivePrograms =
        fieldCurrentValue.filter((item) =>
          inactivePrograms.find(
            (inactiveItem) => inactiveItem.key === item.key,
          ),
        ) || []

      // normalize by key before setting
      const newValue = normalizeByKey([...currentInActivePrograms, ...toArray(value)])
      formik.setFieldValue(field.field_name, newValue)
    }
  }

  const handleDeselectInactiveProgramIds = (value) => {
    const safe = toArray(value)

    // Empty array means all programs are deselected
    if (safe.length === 0) {
      const selectedPrograms = (formik.values[field.field_name] || []).filter(
        (item) =>
          activePrograms.find((activeItem) => activeItem.key === item.key),
      )
      formik.setFieldValue(field.field_name, normalizeByKey(selectedPrograms))
    } else {
      const fieldCurrentValue = formik.values[field.field_name] || []
      // activePrograms should remain as they are
      const currentActivePrograms = fieldCurrentValue.filter((item) => {
        return activePrograms.find((activeItem) => activeItem.key === item.key)
      })
      // value is the deselected inactive programs
      const filteredPrograms = normalizeByKey([
        ...currentActivePrograms,
        ...safe,
      ])
      setInactivePrograms(safe)
      formik.setFieldValue(field.field_name, filteredPrograms)
    }
  }

  const handleMultiSelectRender = (formikField) => {
    const currentFormValues = normalizeByKey(formikField?.value || [])

    const currentInactiveSelectedPrograms = currentFormValues.filter((item) =>
      inactivePrograms.find((inactiveItem) => inactiveItem.key === item.key),
    )

    const renderInactivePrograms = () => {
      const displayInactiveProgramList =
        currentInactiveSelectedPrograms.length > 0

      if (!displayInactiveProgramList) return null

      return (
        <div>
          <label className={styles.label}>Inactive Programs ID(s)</label>
          <div className={styles.description}>
            Inactive programs can only be deselected.
          </div>
          <MultiSelect
            placeholder="Unselect only"
            id={field.field_name}
            name={formikField.name}
            value={currentInactiveSelectedPrograms}
            onChange={(e) => handleDeselectInactiveProgramIds(e.value)}
            onBlur={(e) => formik.handleBlur(e)}
            disabled={isDisabled}
            options={currentInactiveSelectedPrograms}
          />
        </div>
      )
    }

    const currentActiveSelectedPrograms = currentFormValues.filter((item) =>
      activePrograms.find((activeItem) => activeItem.key === item.key),
    )

    return (
      <>
        <MultiSelect
          placeholder="Please select..."
          id={field.field_name}
          name={formikField.name}
          value={currentActiveSelectedPrograms}
          onChange={(e) => handleSelectProgramIds(e.value)}
          onBlur={(e) => formik.handleBlur(e)}
          disabled={isDisabled}
          options={activePrograms}
        />

        {renderInactivePrograms()}
        <ManageHistoryOverlap
          history={programHistory}
          currentSelection={currentFormValues}
          dataIdKey="program_identity_id"
          dataNameKey="program_id_name"
        />
      </>
    )
  }

  if (visible === "none") return null
  if (loading) return <LoaderSmall />

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: visible,
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta, form }) => (
            <>
              {handleMultiSelectRender(formikField)}
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

ManageProgramIdentityIds.propTypes = {
  field: PropTypes.shape({
    field_name: PropTypes.string.isRequired,
    field_label: PropTypes.string.isRequired,
    field_description: PropTypes.string,
    field_validation: PropTypes.shape({
      required: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    read_only: PropTypes.bool,
  }).isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
}

export default ManageProgramIdentityIds
